import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Stack,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { ReportShareCandidate } from "../../../types/reports";

export interface ShareReportFormProps {
  candidates: ReportShareCandidate[];
  /** Share with the picked staff; resolves true on success. */
  onShare: (
    recipientIds: string[],
    syncWithSource: boolean,
  ) => Promise<boolean>;
  /** Called after a successful share (the modal closes). */
  onShared: () => void;
}

export const SYNC_LABEL = "Sync shared reports with original";
export const SYNC_HINT =
  "When you save this report's view, their copy updates to match. Their name and description stay theirs.";

/** "Assistants can't open this report" — the hover hint on a disabled pick. */
export const noAccessHint = (role: string): string =>
  `${role}s can't open this report`;

/**
 * The Share tab: pick staff to send a copy to and choose whether their
 * copies sync with this report. People whose role can't open the report are
 * listed disabled, with the reason on hover.
 */
export default function ShareReportForm({
  candidates,
  onShare,
  onShared,
}: ShareReportFormProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [syncWithSource, setSyncWithSource] = useState(false);
  const [sharing, setSharing] = useState(false);
  const byId = useMemo(
    () => new Map(candidates.map((c) => [c.assignment_id, c])),
    [candidates],
  );
  const options = useMemo(
    () =>
      candidates.map((c) => ({
        value: c.assignment_id,
        label: `${c.name} · ${c.role}`,
        disabled: !c.can_receive,
      })),
    [candidates],
  );

  const submit = async () => {
    setSharing(true);
    const ok = await onShare(selected, syncWithSource);
    setSharing(false);
    if (ok) onShared();
  };

  return (
    <Stack gap="md">
      <MultiSelect
        label="Share with"
        placeholder={options.length ? "Choose staff" : "No one else to add"}
        data={options}
        value={selected}
        onChange={setSelected}
        searchable
        nothingFoundMessage="No matching staff"
        disabled={options.length === 0}
        renderOption={({ option }) => {
          const candidate = byId.get(option.value);
          if (!option.disabled || !candidate) return option.label;
          return (
            <Tooltip
              label={noAccessHint(candidate.role)}
              position="right"
              withArrow
            >
              <Box w="100%">{option.label}</Box>
            </Tooltip>
          );
        }}
        comboboxProps={{ withinPortal: true }}
      />
      <Group gap={6} wrap="nowrap">
        <Checkbox
          label={SYNC_LABEL}
          checked={syncWithSource}
          onChange={(e) => setSyncWithSource(e.currentTarget.checked)}
        />
        <Tooltip label={SYNC_HINT} multiline w={260} withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="About syncing shared reports"
          >
            <IconInfoCircle size={15} aria-hidden />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Group justify="flex-end">
        <Button
          color="teal"
          onClick={submit}
          loading={sharing}
          disabled={selected.length === 0}
        >
          Share
        </Button>
      </Group>
    </Stack>
  );
}
